import SummaryFrame from "~/features/configurator/common/components/ConfigurationSummaryComponent/SummaryFrame/SummaryFrame";
import EditButton from "~/features/configurator/common/components/ConfigurationSummaryComponent/EditButton/EditButton";
import DataDrivenTable from "~/common/components/Table/DataDrivenTable";
import { useSelector } from "react-redux";

import { type RootState } from "~/app/store";
import {
  CSTIC_AL_NAME,
  CSTIC_AR_NAME,
  CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME,
  CSTIC_CARRIAGE_LEFT_NAME,
  CSTIC_CARRIAGE_MIDDLE_NAME,
  CSTIC_CARRIAGE_RIGHT_NAME,
  CSTIC_GUIDEWAY_AMOUNT_NAME,
  CSTIC_LENGTH_NAME,
  CSTIC_PART_AMOUNT_NAME,
  CSTIC_PART_SIZING_TYPE_NAME,
  CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME,
  CSTIC_SCHMIERANSCHLUSS_NAME,
  CSTIC_SCHMIERANSCHLUSS_POSITION_NAME,
  CSTIC_SYSTEM_SMDS,
  CSTIC_WAGENBAUFORM_NAME,
  GET_CARRIAGE_AMOUNT_CSTIC_NAME,
  GET_CARRIAGE_LOCATING_CSTIC_NAME,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  GET_RAIL_LOCATING_CSTIC_NAME,
  LUBRICATION_TILE_SPECIAL_GREASING_PATTERN,
} from "~/features/configurator/Linear/constants";
import {
  getHighestVisitedStep,
  getIsCustomSegmentLengthSelected,
  getKmats,
} from "~/features/configurator/Linear/redux/selectors";
import { type Kmat, type LinearSubstep, type SubstepTile } from "~/features/configurator/Linear/models";
import { getCsticNumberVal } from "~/features/configurator/Linear/utils";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { useTranslation, type WithTranslation } from "react-i18next";
import { isTileStandard } from "~/features/configurator/common/tileUtils";
import { getConfiguratorDefinition } from "~/bridges/configurator/configuratorRegistry";
import { formatCsticValueAsNumber } from "~/features/configurator/Shaft/components/useMinMaxCsticLabel";
import { UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { KmatType } from "~/features/configurator/Linear/kmatType";

const TWO_COL_TABLE_FULL_PAGE = ["30%", "70%"];
const TWO_COL_TABLE_REVIEW = ["50%", "50%"];

const CARRIAGE_GUIDEWAY_TABLE_COLS_FULL_PAGE = ["30%", "17.5%", "17.5%", "17.5%", "17.5%"];
const CARRIAGE_GUIDEWAY_TABLE_COLS_REVIEW = ["33.333%", "33.333%", "33.333%", "33.333%"];

const getSubsteps = (state: RootState) => state.linearConfigurator.substeps;

interface Props {
  isFullReview: boolean;
  onStepChange?: () => void;
}

interface StepData {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  isStandard: boolean;
}

const selectStepName = (index: number) => (state: RootState) => {
  return state.linearConfigurator.steps[index - 1]?.name ?? "";
};

export const LinearSummaryData = ({ isFullReview, onStepChange }: Props) => {
  const { t } = useTranslation();
  const xmlT = useXmlConfiguratorTranslations().t!;
  const dataTableHeader = [t("linearConfigSummary.step.header")];

  const twoColTable = isFullReview ? TWO_COL_TABLE_FULL_PAGE : TWO_COL_TABLE_REVIEW;
  const carriageGuidewayTableColSizes = isFullReview
    ? CARRIAGE_GUIDEWAY_TABLE_COLS_FULL_PAGE
    : CARRIAGE_GUIDEWAY_TABLE_COLS_REVIEW;

  //1
  const step1Data = useSelector(prepareStep1Data);
  const step2Data = useSelector(prepareStep2Data);
  const step3DataTable = useSelector(prepareStep3Data);
  const step4DataTable = useSelector(prepareStep4Data(step1Data?.guidewayAmount, t));
  const step5DataTable = useSelector(prepareStep5Data);
  const { dataTable: step6DataTable, header: dataTableHeader6Step } = useSelector(
    prepareStep6Data(step1Data?.guidewayAmount, t),
  );
  const {
    firstTable: step7FirstTable,
    secondTable: step7SecondTable,
    header: dataTableHeader7Step,
  } = useSelector(prepareStep7Data(step1Data?.guidewayAmount, step1Data?.carriageAmount, t));

  const step1Name = xmlT(useSelector(selectStepName(1)));
  const step2Name = xmlT(useSelector(selectStepName(2)));
  const step3Name = xmlT(useSelector(selectStepName(3)));
  const step4Name = xmlT(useSelector(selectStepName(4)));
  const step5Name = xmlT(useSelector(selectStepName(5)));
  const step6Name = xmlT(useSelector(selectStepName(6)));
  const step7Name = xmlT(useSelector(selectStepName(7)));

  return (
    <>
      {step1Data && (
        <SummaryFrame num={1} name={step1Name}>
          <div className="left-inside">
            <h3 className="pb-0">
              <div>
                {t("linearConfigSummary.guideway.info", {
                  count: step1Data.guidewayAmount,
                })}
              </div>
              <div>
                {t("linearConfigSummary.carriages.info", {
                  count: step1Data.carriageAmount,
                })}
              </div>
            </h3>

            <EditButton index={0} onStepChange={onStepChange} />
          </div>
          <div className="right-inside">
            <img src={xmlT(step1Data.imageUrl)} alt="linear configuration product" />
          </div>
        </SummaryFrame>
      )}
      {step2Data && (
        <SummaryFrame num={2} name={step2Name}>
          <h3 className="pb-0">
            <div>{step2Data.baseDesignation}</div>
          </h3>

          <EditButton index={1} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
      {step3DataTable && (
        <SummaryFrame num={3} name={step3Name}>
          <DataDrivenTable header={dataTableHeader} data={step3DataTable} columnFlex={twoColTable} />
          <EditButton index={2} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
      {step4DataTable && (
        <SummaryFrame num={4} name={step4Name}>
          <DataDrivenTable header={dataTableHeader} data={step4DataTable} columnFlex={twoColTable} />
          <EditButton index={3} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
      {step5DataTable && (
        <SummaryFrame num={5} name={step5Name}>
          <DataDrivenTable header={dataTableHeader} data={step5DataTable} columnFlex={twoColTable} />
          <EditButton index={4} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
      {step6DataTable && dataTableHeader6Step && (
        <SummaryFrame num={6} name={step6Name}>
          <DataDrivenTable
            header={dataTableHeader6Step}
            data={step6DataTable[0]}
            columnFlex={carriageGuidewayTableColSizes}
          />
          <EditButton index={5} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
      {((step7FirstTable && step7FirstTable.length > 0) || step7SecondTable) && (
        <SummaryFrame num={7} name={step7Name}>
          {step7FirstTable && step7FirstTable.length > 0 && (
            <DataDrivenTable header={dataTableHeader} data={step7FirstTable} columnFlex={twoColTable} />
          )}

          {step7SecondTable && dataTableHeader7Step && (
            <>
              {step7SecondTable.map((guidewayData, index) => (
                <div className={"d-flex flex-direction-column"}>
                  <h3 className={"mb-2 scha__lc-summary__frame__title"}>
                    {t("linearConfigSummary.step.headerGuidewaySet", { count: index + 1 })}
                  </h3>
                  <DataDrivenTable
                    header={dataTableHeader7Step[index]}
                    data={guidewayData}
                    columnFlex={carriageGuidewayTableColSizes}
                  />
                </div>
              ))}
            </>
          )}
          <EditButton index={6} onStepChange={onStepChange} />
        </SummaryFrame>
      )}
    </>
  );
};

export const prepareStep1Data = (state: RootState) => {
  const systemLayout = state.linearConfigurator.substeps.SystemLayout;

  if (!systemLayout || systemLayout.selectedTile === null) {
    return null;
  }
  const selectedTile = systemLayout.tiles[systemLayout.selectedTile];
  const imageUrl = selectedTile.imageUrl;

  const guidewayAmount = Number(selectedTile.cstics[CSTIC_GUIDEWAY_AMOUNT_NAME] ?? 0);
  const carriageAmount = Number(selectedTile.cstics[GET_CARRIAGE_AMOUNT_CSTIC_NAME(1)] ?? 0);

  return {
    guidewayAmount,
    carriageAmount,
    imageUrl,
  };
};

const prepareStep2Data = (state: RootState) => {
  const highestVisitedStep = getHighestVisitedStep(state);
  if (highestVisitedStep === 0) {
    return null;
  }
  return {
    baseDesignation: getConfiguratorDefinition(UNIT_CONFIGURATOR_PRODUCT_CODE).generateDesignation!(
      state.linearConfigurator.kmats,
    ),
  };
};

export const generateSimpleSubstepData = (step: LinearSubstep | undefined, kmats: Dict<Kmat[]>) => {
  if (!step || step.selectedTile === null) {
    return null;
  }
  const allTiles = [...step.tiles];
  step.groupedTiles?.forEach((group) => allTiles.push(...group.tiles));
  const selectedTile = allTiles[step.selectedTile];
  return {
    name: step.name,
    value: selectedTile.name,
    isStandard: isTileStandard(selectedTile, kmats),
  };
};
export const generateClosureSubstepData = (
  step: LinearSubstep | undefined,
  kmats: Dict<Kmat[]>,
  t: WithTranslation["t"],
) => {
  if (!step || step.selectedTile === null) {
    return null;
  }
  const allTiles = [...step.tiles];
  step.groupedTiles?.forEach((group) => allTiles.push(...group.tiles));
  const selectedTile = allTiles[step.selectedTile];
  return {
    name: step.name,
    value: (
      <div>
        <LanguageAwareExternalContent content={selectedTile.name} className={"scha__lc__external-label"} />
        <LanguageAwareExternalContent content={selectedTile.description} className={"scha__lc__external-label"} />
      </div>
    ),
    isStandard: isTileStandard(selectedTile, kmats),
  };
};

const prepareStep3Data = (state: RootState) => {
  const highestVisitedStep = getHighestVisitedStep(state);
  if (highestVisitedStep < 2) {
    // 3 - 1, because 0 indexed
    return null;
  }

  const substeps = getSubsteps(state);
  const kmats = getKmats(state);

  const step3data = [];

  const systemCoatingData = generateSimpleSubstepData(substeps.SystemCoating, kmats);
  if (systemCoatingData) {
    step3data.push(systemCoatingData);
  }

  const lengthValue = substeps?.Length.value ?? null;
  if (lengthValue !== null) {
    step3data.push({
      name: substeps.Length.name,
      value: lengthValue,
      isStandard: false,
    });
  }

  const preloadData = generateSimpleSubstepData(substeps.Preload, kmats);
  if (preloadData) {
    step3data.push(preloadData);
  }

  const accuracyData = generateSimpleSubstepData(substeps.Accuracy, kmats);
  if (accuracyData) {
    step3data.push(accuracyData);
  }

  const heightSorting = generateSimpleSubstepData(substeps.HeightSorting, kmats);
  if (heightSorting && substeps.HeightSorting.isVisible) {
    step3data.push(heightSorting);
  }

  if (step3data.length > 0) {
    return prepareTableView(step3data);
  } else {
    return null;
  }
};

const prepareStep4Data =
  (amountOfGuideways: number | undefined | null = null, t: WithTranslation["t"]) =>
  (state: RootState) => {
    if (!amountOfGuideways) {
      return null;
    }

    const highestVisitedStep = getHighestVisitedStep(state);
    if (highestVisitedStep < 3) {
      // 4 - 1, because 0 indexed
      return null;
    }

    const defaultKmatCstics = getMainKmat({ kmats: state.linearConfigurator.kmats }).cstics;

    const step4data = [];

    for (let i = 1; i <= amountOfGuideways; i++) {
      const railCsticName = GET_RAIL_LOCATING_CSTIC_NAME(i);
      const carriageCsticName = GET_CARRIAGE_LOCATING_CSTIC_NAME(i);

      const railCstic = defaultKmatCstics[railCsticName];
      if (defaultKmatCstics[railCsticName].value !== null) {
        const val = railCstic.values.find((val) => val.value === railCstic.value);
        step4data.push({
          name: t(`configurator.unit.cstic.railLocatingFace${i}.name`),
          value: val?.label ?? railCstic.value,
          isStandard: railCstic.value === "O",
        });
      }

      const carriageCstic = defaultKmatCstics[carriageCsticName];
      if (carriageCstic.value !== null) {
        const val = carriageCstic.values.find((val) => val.value === carriageCstic.value);
        step4data.push({
          name: t(`configurator.unit.cstic.carriageLocatingFace${i}.name`),
          value: val?.label ?? carriageCstic.value,
          isStandard: carriageCstic.value === "O",
        });
      }
    }

    if (step4data.length > 0) {
      return prepareTableView(step4data);
    } else {
      return null;
    }
  };

const prepareStep5Data = (state: RootState) => {
  const highestVisitedStep = getHighestVisitedStep(state);
  if (highestVisitedStep < 3) {
    // 4 - 1, because 0 indexed
    return null;
  }

  //5
  const step5data = [];

  const substeps = getSubsteps(state);
  const kmats = getKmats(state);

  const lubrication = substeps.Lubrication;
  if (lubrication && lubrication.selectedTile !== null) {
    const allTiles: SubstepTile[] = [...lubrication.tiles];
    lubrication.groupedTiles.forEach((group) => allTiles.push(...group.tiles));

    const selectedTile = allTiles[lubrication.selectedTile];
    step5data.push({
      name: substeps.Lubrication.name,
      value: selectedTile.name.includes(LUBRICATION_TILE_SPECIAL_GREASING_PATTERN)
        ? selectedTile.description
        : selectedTile.name,
      isStandard: isTileStandard(selectedTile, kmats),
    });
  }

  const attachmentData = generateSimpleSubstepData(substeps.Attachment, kmats);
  if (attachmentData) {
    step5data.push(attachmentData);
  }

  const closure = substeps.Closure;
  if (closure && closure.selectedTile !== null && closure.isVisible) {
    const allTiles: SubstepTile[] = [];
    closure.groupedTiles.forEach((group) => allTiles.push(...group.tiles));

    const selectedTile = allTiles[closure.selectedTile];
    step5data.push({
      name: substeps.Closure.name,
      value: selectedTile.name,
      isStandard: isTileStandard(selectedTile, kmats),
    });
  }

  if (step5data.length > 0) {
    return prepareTableView(step5data);
  } else {
    return null;
  }
};

const prepareStep6Data =
  (amountOfGuideways: number | undefined | null = null, t: WithTranslation["t"]) =>
  (state: RootState) => {
    if (!amountOfGuideways) {
      return {};
    }

    const highestVisitedStep = getHighestVisitedStep(state);
    if (highestVisitedStep < 3) {
      // 4 - 1, because 0 indexed
      return {};
    }

    const kmats = getKmats(state);

    const allStep6data: StepData[][] = [];

    const dataTableHeader6Step = [t("linearConfigSummary.step.header")];

    for (let i = 1; i <= amountOfGuideways; i++) {
      const index = i - 1;
      dataTableHeader6Step[i] = t("linearConfigSummary.step.headerGuidewaySet", { count: i });
      const step6data = [];
      if (kmats[KmatType.SubRail][index].cstics[CSTIC_LENGTH_NAME].value !== null) {
        const length = kmats[KmatType.SubRail][index].cstics[CSTIC_LENGTH_NAME].value;
        step6data.push({
          name: t("linearConfigSummary.step6.1lane"),
          value: formatCsticValueAsNumber(length),
          isStandard: false,
        });
      }
      if (kmats[KmatType.SubRail][index].cstics[CSTIC_AL_NAME].value !== null) {
        const alValue = getCsticNumberVal(kmats[KmatType.SubRail][index].cstics[CSTIC_AL_NAME]);
        step6data.push({
          name: t("linearConfigSummary.step6.2lane"),
          value:
            alValue !== null ? NF.getDefaultFormatter().format(Math.round((alValue + Number.EPSILON) * 100) / 100) : "",
          isStandard: false,
        });
      }
      if (kmats[KmatType.SubRail][index].cstics[CSTIC_AR_NAME].value !== null) {
        const arValue = getCsticNumberVal(kmats[KmatType.SubRail][index].cstics[CSTIC_AR_NAME]);
        step6data.push({
          name: t("linearConfigSummary.step6.3lane"),
          value:
            arValue !== null ? NF.getDefaultFormatter().format(Math.round((arValue + Number.EPSILON) * 100) / 100) : "",
          isStandard: false,
        });
      }

      if (kmats[KmatType.SubRail][index].cstics[CSTIC_PART_SIZING_TYPE_NAME].value !== null) {
        const customSegmentLengthSelected = getIsCustomSegmentLengthSelected(index)(state);
        if (customSegmentLengthSelected) {
          step6data.push({
            name: t("linearConfigSummary.step6.4lane"),
            value: kmats[KmatType.SubRail][index].cstics[CSTIC_PART_SIZING_TYPE_NAME].values.find(
              (v) => v.value === kmats[KmatType.SubRail][index].cstics[CSTIC_PART_SIZING_TYPE_NAME].value,
            )!.label,
            isStandard: false,
          });
        } else {
          step6data.push({
            name: t("linearConfigSummary.step6.4lane"),
            value: "-",
            isStandard: false,
          });
        }
      }

      //PARTS
      const numberOfPartsValue = kmats[KmatType.SubRail][index].cstics[CSTIC_PART_AMOUNT_NAME]
        ? t("linearConfigSummary.step6.Partsvalue", {
            count: Number(kmats[KmatType.SubRail][index].cstics[CSTIC_PART_AMOUNT_NAME].value),
          })
        : "-";
      step6data.push({
        name: t("linearConfigSummary.step6.5lane"),
        value: numberOfPartsValue,
        isStandard: false,
      });
      for (let j = 1; j <= 5; j++) {
        const cstic = kmats[KmatType.SubRail][index].cstics[GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(j)];
        const csticNumericValue = getCsticNumberVal(cstic);
        step6data.push({
          name: t("linearConfigSummary.steps6.partialLength", { count: j }),
          value: csticNumericValue !== null ? NF.getDefaultFormatter().format(csticNumericValue) : "-",
          isStandard: false,
        });
      }

      allStep6data.push(step6data);
    }

    if (allStep6data.length > 0) {
      return {
        dataTable: prepareFullTableView(allStep6data, amountOfGuideways),
        header: dataTableHeader6Step,
      };
    } else {
      return {};
    }
  };

const prepareStep7Data =
  (
    amountOfGuideways: number | undefined | null = null,
    amountOfCarriages: number | undefined | null = null,
    t: WithTranslation["t"],
  ) =>
  (state: RootState) => {
    if (!amountOfGuideways || !amountOfCarriages) {
      return {};
    }

    const highestVisitedStep = getHighestVisitedStep(state);
    if (highestVisitedStep < 3) {
      // 4 - 1, because 0 indexed
      return {};
    }

    const substeps = getSubsteps(state);
    const kmats = getKmats(state);

    //7
    // first table (step 7)
    const step7dataFirst = [];
    const firstCarriage = kmats[KmatType.SubCarriage][0].cstics[CSTIC_WAGENBAUFORM_NAME];
    step7dataFirst.push({
      name: substeps.ReferenceCarriage.name,
      value:
        substeps.ReferenceCarriage.selectedTile !== null
          ? substeps.ReferenceCarriage.tiles[substeps.ReferenceCarriage.selectedTile].name
          : t("linearConfigSummary.steps7.seeCarriageDetails"),
      isStandard: substeps.ReferenceCarriage.selectedTile === 0,
    });

    const defaultKmatCstics = getMainKmat({ kmats: state.linearConfigurator.kmats }).cstics;
    const smdsCstic = defaultKmatCstics[CSTIC_SYSTEM_SMDS];

    if (substeps.SMDS?.isVisible && smdsCstic.value != null) {
      step7dataFirst.push({
        name: substeps.SMDS.name,
        value: substeps.SMDS.tiles[substeps.SMDS.selectedTile!].name,
        isStandard: smdsCstic.value == "N",
      });
    }

    const step7dataTableFirst = prepareTableView(step7dataFirst);

    //second table (step 7)
    const dataTableHeader7Step: string[][] = [];

    const allGuidewaysData: StepData[][] = [];
    for (let j = 1; j <= amountOfGuideways; j++) {
      const guidewayIndex = j - 1;
      dataTableHeader7Step.push([t("linearConfigSummary.step.header")]);

      /*
        LC KB will not send cstics for VSARBW_065 and VSW_065 only for certain combinations.
        If for one carriage subkmat you have these cstics, but for the others you don't, columns in the table will shift
        and show incorrect information.
        In this block we'll check if there's at least one subkmat with values for these characteristics and also retain
        the name for them
     */
      const carriageIndexesInThisGuideway: number[] = [];
      for (let i = 1; i <= amountOfCarriages; i++) {
        const carriageIndex = i - 1;
        const realIndexInState = carriageIndex + guidewayIndex * amountOfCarriages;
        carriageIndexesInThisGuideway.push(realIndexInState);
      }
      let atLeastOneLubricationConnectorPosition = false;
      let atLeastOneLubricationDegree = false;
      let atLeastOneLubricationPort = false;
      let lubricationConnectorPositionLabel = "";
      let lubricationDegreeLabel = "";
      let lubricationPortLabel = "";
      kmats[KmatType.SubCarriage].forEach((item, currentIndex) => {
        if (carriageIndexesInThisGuideway.indexOf(currentIndex) !== -1) {
          if (item.cstics[CSTIC_SCHMIERANSCHLUSS_POSITION_NAME]) {
            atLeastOneLubricationConnectorPosition = true;
            lubricationConnectorPositionLabel = item.cstics[CSTIC_SCHMIERANSCHLUSS_POSITION_NAME].label;
          }
          if (item.cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME]) {
            atLeastOneLubricationDegree = true;
            lubricationDegreeLabel = item.cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME].label;
          }
          if (item.cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME]) {
            atLeastOneLubricationPort = true;
            lubricationPortLabel = item.cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME].label;
          }
        }
      });

      for (let i = 1; i <= amountOfCarriages; i++) {
        const carriageIndex = i - 1;
        const realIndexInState = carriageIndex + guidewayIndex * amountOfCarriages;
        dataTableHeader7Step[j - 1][i] = t("linearConfigSummary.step7.carriage", { count: j + i / 10 });
        const step7data = [];

        const cstics = kmats[KmatType.SubCarriage][realIndexInState].cstics;

        const singleCarriageReference = cstics[CSTIC_WAGENBAUFORM_NAME];
        const carriageRefSelectedTile = singleCarriageReference.values.find(
          (singleValue) => singleValue.value === singleCarriageReference.value,
        );
        if (singleCarriageReference.value !== null && singleCarriageReference.value !== undefined) {
          step7data.push({
            name: singleCarriageReference.label,
            value: carriageRefSelectedTile?.label,
            isStandard: carriageRefSelectedTile?.standard || false,
          });
        }

        const kitLeft = cstics[CSTIC_CARRIAGE_LEFT_NAME];
        if (kitLeft && kitLeft.value !== null && kitLeft.value !== undefined) {
          step7data.push({
            name: kitLeft.label,
            value: kitLeft.value,
            isStandard: false,
          });
        }

        const kitMid = cstics[CSTIC_CARRIAGE_MIDDLE_NAME];
        if (kitMid && kitMid.value !== null && kitMid.value !== undefined) {
          step7data.push({
            name: kitMid.label,
            value: kitMid.value,
            isStandard: false,
          });
        }

        const kitRight = cstics[CSTIC_CARRIAGE_RIGHT_NAME];
        if (kitRight && kitRight.value !== null && kitRight.value !== undefined) {
          step7data.push({
            name: kitRight.label,
            value: kitRight.value,
            isStandard: false,
          });
        }

        const lubrication = cstics[CSTIC_SCHMIERANSCHLUSS_NAME];
        if (lubrication) {
          const lubricationSelectedTile = lubrication.values.find(
            (singleValue) => singleValue.value === lubrication.value,
          );
          if (lubrication.value !== null) {
            step7data.push({
              name: lubrication.label,
              value: lubricationSelectedTile!.label,
              isStandard: lubricationSelectedTile!.standard!,
            });
          }
        }

        const lubricationPosition = cstics[CSTIC_SCHMIERANSCHLUSS_POSITION_NAME];
        if (lubricationPosition) {
          const lubricationPositionSelectedTile =
            lubricationPosition !== undefined
              ? lubricationPosition.values.find((singleValue) => singleValue.value === lubricationPosition.value)
              : { label: "-", standard: false };
          step7data.push({
            name: lubricationPosition.label,
            value: lubricationPositionSelectedTile!.label,
            isStandard: lubricationPositionSelectedTile!.standard!,
          });
        } else if (atLeastOneLubricationConnectorPosition) {
          step7data.push({
            name: lubricationConnectorPositionLabel,
            value: "-",
            isStandard: false,
          });
        }

        const lubricationPortSelection = cstics[CSTIC_AUSWAHL_SCHMIERANSCHLUSS_NAME];
        if (lubricationPortSelection) {
          const lubricationPortSelectionSelectedTile =
            lubricationPortSelection !== undefined
              ? lubricationPortSelection.values.find(
                  (singleValue) => singleValue.value === lubricationPortSelection.value,
                )
              : { label: "-", standard: false };
          step7data.push({
            name: lubricationPortSelection.label,
            value: lubricationPortSelectionSelectedTile!.label,
            isStandard: lubricationPortSelectionSelectedTile!.standard!,
          });
        } else if (atLeastOneLubricationPort) {
          step7data.push({
            name: lubricationPortLabel,
            value: "-",
            isStandard: false,
          });
        }

        const lubricationDegree = cstics[CSTIC_SCHMIERANSCHLUSS_DEGEEE_NAME];
        if (lubricationDegree) {
          const lubricationDegreeSelectedTile =
            lubricationDegree !== undefined
              ? lubricationDegree.values.find((singleValue) => singleValue.value === lubricationDegree.value)
              : { label: "-", standard: false };
          step7data.push({
            name: lubricationDegree.label,
            value: lubricationDegreeSelectedTile!.label,
            isStandard: lubricationDegreeSelectedTile!.standard!,
          });
        } else if (atLeastOneLubricationDegree) {
          step7data.push({
            name: lubricationDegreeLabel,
            value: "-",
            isStandard: false,
          });
        }

        allGuidewaysData.push(step7data);
      }
    }
    const step7dataTable = prepareFullTableView(allGuidewaysData, amountOfCarriages);

    return {
      firstTable: step7dataTableFirst,
      secondTable: step7dataTable,
      header: dataTableHeader7Step,
    };
  };

export const prepareTableView = (stepData: StepData[]) => {
  return stepData.map((row) => {
    return [
      <div className="pr-60">
        <LanguageAwareExternalContent content={row.name} className={"scha__lc__external-label"} />
      </div>,
      <div className="p-relative d-flex">
        {row.isStandard ? <span className="scha-icon-star f-green icon-margin" /> : ""}
        {typeof row.value === "string" ? (
          <LanguageAwareExternalContent content={row.value} className={"scha__lc__external-label"} />
        ) : (
          row.value
        )}
      </div>,
    ];
  });
};

export const prepareFullTableView = (stepData: StepData[][], numberOfDataInLine: number) => {
  const countHowManyTabs = stepData.length / numberOfDataInLine;
  const tableResult = [];
  let tabIndex = 0;
  for (let i = 0; i < countHowManyTabs; i++) {
    const numberOfRows = stepData[tabIndex].length;
    const tableView = [];
    for (let x = 0; x < numberOfRows; x++) {
      const tableRow = [];
      tableRow.push(
        <div className="pr-60">
          <LanguageAwareExternalContent
            content={stepData[tabIndex][x]?.name}
            className={"scha__lc__external-label scha__bold-b-gone"}
          />
        </div>,
      );

      for (let j = 0; j < numberOfDataInLine; j++) {
        const index = j + i * numberOfDataInLine;
        tableRow.push(
          <div className="p-relative d-flex">
            {stepData[index][x]?.isStandard ? <span className="scha-icon-star f-green icon-margin" /> : ""}
            {typeof stepData[index][x]?.value === "string" ? (
              <LanguageAwareExternalContent
                content={stepData[index][x]?.value}
                className={"scha__lc__external-label"}
              />
            ) : (
              ""
            )}
          </div>,
        );
      }
      tableView.push(tableRow);
    }

    tableResult.push(tableView);
    tabIndex += numberOfDataInLine;
  }
  return tableResult;
};
